import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/Card';
import { Table, Column } from '../../components/Table';
import { Pagination } from '../../components/Pagination';
import type { DirectoryUserListItem, DirectoryUserListResponse, DirectoryUserStatusFilter } from '../../types/api';
import { useAdminResource } from './useAdminResource';
import { DirectoryError, DirectorySearch, pageFromParam } from './parts';
import { DIRECTORY_PAGE_SIZE } from './labels';
import { PageHeader, TableSkeleton, primaryButton } from '../../components/admin/AdminUI';
import { Chip, FilterTabs, IdentityCell, RelativeTime, StatusPill, VerifiedIcon } from '../../components/admin/TableCells';
import { CreateUserDialog, UserActions } from './UserManagement';

const STATUS_FILTERS: Array<{ value: DirectoryUserStatusFilter; label: string }> = [
  { value: 'all', label: 'Everyone' },
  { value: 'signed_in', label: 'Signed in now' },
  { value: 'active', label: 'Active' },
  { value: 'deactivated', label: 'Deactivated' },
];

const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name', label: 'Name A–Z' },
  { value: 'email', label: 'Email A–Z' },
] as const;
type Sort = (typeof SORTS)[number]['value'];

const PAGE_SIZES = [10, 25, 50, 100];

function statusFromParam(raw: string | null): DirectoryUserStatusFilter {
  return STATUS_FILTERS.some((f) => f.value === raw) ? (raw as DirectoryUserStatusFilter) : 'all';
}
function sortFromParam(raw: string | null): Sort {
  return SORTS.some((s) => s.value === raw) ? (raw as Sort) : 'newest';
}
function limitFromParam(raw: string | null): number {
  const n = raw ? parseInt(raw, 10) : NaN;
  return PAGE_SIZES.includes(n) ? n : DIRECTORY_PAGE_SIZE;
}

/** One status pill per person, plus a temporary-password chip. */
export function UserStatusCell({ user }: { user: DirectoryUserListItem }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {user.deactivatedAt ? (
        <StatusPill tone="red" label="Deactivated" />
      ) : (user.activeSessions ?? 0) > 0 ? (
        <StatusPill tone="blue" label="Signed in now" pulse />
      ) : (
        <StatusPill tone="green" label="Active" />
      )}
      {user.mustChangePassword && !user.deactivatedAt && <Chip tone="amber" label="Temp password" />}
    </div>
  );
}

type UserListResponse = DirectoryUserListResponse & { counts?: Record<DirectoryUserStatusFilter, number> };

/**
 * Admin console: every Central user, who is signed in now, and an action menu
 * per person. Search, status tab, sort, page and page size live in the URL so
 * Back from a person's page returns to the same list.
 */
export function UsersPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const q = params.get('q') ?? '';
  const status = statusFromParam(params.get('status'));
  const sort = sortFromParam(params.get('sort'));
  const page = pageFromParam(params.get('page'));
  const limit = limitFromParam(params.get('limit'));
  const [creating, setCreating] = useState(false);

  const query = new URLSearchParams();
  if (q) query.set('q', q);
  if (status !== 'all') query.set('status', status);
  if (sort !== 'newest') query.set('sort', sort);
  query.set('page', String(page));
  query.set('limit', String(limit));
  const { data, error, loading, reload } = useAdminResource<UserListResponse>(`/users?${query.toString()}`);

  const backTo = { from: location.pathname + location.search };
  const open = (row: DirectoryUserListItem) => navigate(`/users/${row.id}`, { state: backTo });

  const setSearch = (next: { q?: string; page?: number; status?: DirectoryUserStatusFilter; sort?: Sort; limit?: number }) => {
    const p = new URLSearchParams();
    const nextQ = next.q ?? q;
    if (nextQ) p.set('q', nextQ);
    const nextStatus = next.status ?? status;
    if (nextStatus !== 'all') p.set('status', nextStatus);
    const nextSort = next.sort ?? sort;
    if (nextSort !== 'newest') p.set('sort', nextSort);
    const nextLimit = next.limit ?? limit;
    if (nextLimit !== DIRECTORY_PAGE_SIZE) p.set('limit', String(nextLimit));
    const nextPage = next.page ?? 1;
    if (nextPage > 1) p.set('page', String(nextPage));
    setParams(p);
  };

  const columns: Column<DirectoryUserListItem>[] = [
    {
      key: 'person',
      header: 'Person',
      className: 'min-w-[16rem]',
      render: (row) => (
        <IdentityCell
          id={row.id}
          name={row.name || row.email}
          primary={
            <Link to={`/users/${row.id}`} state={backTo} onClick={(e) => e.stopPropagation()} className="hover:text-emerald-700 hover:underline">
              {row.name || row.email}
            </Link>
          }
          secondary={row.name ? row.email : undefined}
        />
      ),
    },
    { key: 'status', header: 'Status', render: (row) => <UserStatusCell user={row} /> },
    {
      key: 'workspaceCount',
      header: 'Workspaces',
      className: 'text-right tabular-nums',
      render: (row) => <span className="font-medium text-slate-900">{row.workspaceCount}</span>,
    },
    {
      key: 'access',
      header: 'Access',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.isCentralAdmin && <Chip tone="blue" label="Central admin" />}
          {row.isPlatformInternal && <Chip tone="slate" label="Internal" />}
          {!row.isCentralAdmin && !row.isPlatformInternal && <span className="text-slate-400">—</span>}
        </div>
      ),
    },
    { key: 'isEmailVerified', header: 'Verified', className: 'text-center', render: (row) => <VerifiedIcon verified={row.isEmailVerified} /> },
    { key: 'createdAt', header: 'Joined', render: (row) => <RelativeTime iso={row.createdAt} never="—" /> },
    { key: 'lastSignInAt', header: 'Last sign-in', render: (row) => <RelativeTime iso={row.lastSignInAt} /> },
    { key: 'actions', header: '', render: (row) => <UserActions user={row} onChanged={reload} /> },
  ];

  const counts = data?.counts;
  return (
    <div className="space-y-6">
      <PageHeader
        title="Users"
        subtitle={<>Everyone with a Central account, who is signed in now, and what you can change for each person.</>}
        actions={
          <button type="button" className={primaryButton} onClick={() => setCreating(true)}>
            Add person
          </button>
        }
      />

      <Card>
        <div className="space-y-4">
          <DirectorySearch label="Search users" placeholder="Search by name or email" value={q} onSearch={(next) => setSearch({ q: next, page: 1 })} />
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <FilterTabs
              label="Filter by status"
              value={status}
              onChange={(v) => setSearch({ status: v, page: 1 })}
              options={STATUS_FILTERS.map((f) => ({ ...f, count: counts ? counts[f.value] : null }))}
            />
            <div className="flex items-center gap-3">
              {data && (
                <p className="text-sm text-slate-500" data-testid="directory-count">
                  {q
                    ? `${data.pagination.total} ${data.pagination.total === 1 ? 'user matches' : 'users match'} “${q}”`
                    : `${data.pagination.total} ${data.pagination.total === 1 ? 'user' : 'users'}`}
                </p>
              )}
              <label className="flex items-center gap-2 text-sm text-slate-500">
                <span className="sr-only sm:not-sr-only">Sort</span>
                <select
                  aria-label="Sort users"
                  value={sort}
                  onChange={(e) => setSearch({ sort: e.target.value as Sort, page: 1 })}
                  className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-700 shadow-sm"
                >
                  {SORTS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {loading ? (
            <TableSkeleton message="Loading users…" cols={6} />
          ) : error ? (
            <DirectoryError error={error} what="Users" onRetry={reload} />
          ) : data ? (
            <>
              <Table<DirectoryUserListItem>
                columns={columns}
                data={data.users}
                keyField="id"
                onRowClick={open}
                emptyMessage={q ? `No users match “${q}”.` : status === 'all' ? 'No users yet.' : 'Nobody here right now.'}
              />
              <Pagination
                total={data.pagination.total}
                limit={data.pagination.limit}
                offset={(data.pagination.page - 1) * data.pagination.limit}
                onPageChange={(offset) => setSearch({ page: Math.floor(offset / data.pagination.limit) + 1 })}
                onLimitChange={(n) => setSearch({ limit: n, page: 1 })}
                pageSizes={PAGE_SIZES}
              />
            </>
          ) : null}
        </div>
      </Card>

      <CreateUserDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false);
          navigate(`/users/${id}`, { state: backTo });
        }}
      />
    </div>
  );
}
